import type { FormEvent } from 'react';
import type { ClubMemberRole } from '../../api';
import { canHaveOwnershipShare } from '../../utils/isGeneralUser';
import ModalOverlay from '../modal/ModalOverlay';
import ModalPortal from '../modal/ModalPortal';
import { todayIso } from './membersUtils';

type AddTab = 'invite' | 'manual';

type AddForm = {
  name: string;
  login: string;
  password: string;
  role: ClubMemberRole;
  joined_on: string;
};

type RoleOption = { value: ClubMemberRole; label: string };

type Props = {
  addTab: AddTab;
  inviteRole: ClubMemberRole;
  inviteLink: string | null;
  form: AddForm;
  adding: boolean;
  createRoleOptions: RoleOption[];
  onTabChange: (tab: AddTab) => void;
  onInviteRoleChange: (role: ClubMemberRole) => void;
  onInvite: () => void;
  onCopyInvite: () => void;
  onFormChange: (form: AddForm) => void;
  onAdd: (e: FormEvent) => void;
  onClose: () => void;
};

export default function AddMemberModal({
  addTab,
  inviteRole,
  inviteLink,
  form,
  adding,
  createRoleOptions,
  onTabChange,
  onInviteRoleChange,
  onInvite,
  onCopyInvite,
  onFormChange,
  onAdd,
  onClose,
}: Props) {
  return (
    <ModalPortal>
      <ModalOverlay onClose={onClose}>
        <div
          className="modal members-add-modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="members-add-modal-title"
        >
          <h3 id="members-add-modal-title">Добавить участника</h3>
          <div className="members-add-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={addTab === 'invite'}
              className={
                addTab === 'invite'
                  ? 'members-add-tab active'
                  : 'members-add-tab'
              }
              onClick={() => onTabChange('invite')}
            >
              По ссылке
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={addTab === 'manual'}
              className={
                addTab === 'manual'
                  ? 'members-add-tab active'
                  : 'members-add-tab'
              }
              onClick={() => onTabChange('manual')}
            >
              Вручную
            </button>
          </div>

          {addTab === 'invite' ? (
            <div className="members-add-tab-panel">
              <p className="members-invite-desc">
                Выберите роль и создайте ссылку — новый участник сам заполнит
                данные.
              </p>
              <div className="members-invite-controls">
                <label className="members-field">
                  <span>Роль</span>
                  <select
                    value={inviteRole}
                    onChange={(e) => {
                      onInviteRoleChange(e.target.value as ClubMemberRole);
                    }}
                  >
                    {createRoleOptions.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="btn-invite"
                  onClick={onInvite}
                >
                  Создать ссылку
                </button>
              </div>
              {inviteLink ? (
                <div className="members-invite-share">
                  <input
                    className="members-invite-link"
                    type="text"
                    readOnly
                    value={inviteLink}
                    onFocus={(e) => e.currentTarget.select()}
                    aria-label="Ссылка-приглашение"
                  />
                  <button
                    type="button"
                    className="btn-invite-copy"
                    onClick={onCopyInvite}
                  >
                    Скопировать
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <form className="members-add-form" onSubmit={onAdd}>
              <label className="members-field">
                <span>Имя</span>
                <input
                  type="text"
                  placeholder="Имя"
                  value={form.name}
                  onChange={(e) => onFormChange({ ...form, name: e.target.value })}
                  maxLength={100}
                />
              </label>
              <label className="members-field">
                <span>Логин</span>
                <input
                  type="text"
                  placeholder="Логин"
                  value={form.login}
                  onChange={(e) =>
                    onFormChange({ ...form, login: e.target.value })
                  }
                  maxLength={50}
                  autoComplete="off"
                />
              </label>
              <label className="members-field">
                <span>Пароль</span>
                <input
                  type="password"
                  placeholder="Пароль"
                  value={form.password}
                  onChange={(e) =>
                    onFormChange({ ...form, password: e.target.value })
                  }
                  autoComplete="new-password"
                  minLength={4}
                />
              </label>
              <label className="members-field">
                <span>Роль</span>
                <select
                  value={form.role}
                  onChange={(e) =>
                    onFormChange({
                      ...form,
                      role: e.target.value as ClubMemberRole,
                    })
                  }
                >
                  {createRoleOptions.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>
              {canHaveOwnershipShare(form.role) ? (
                <label className="members-field members-field-joined">
                  <span>Собственник с</span>
                  <input
                    type="date"
                    value={form.joined_on}
                    max={todayIso()}
                    onChange={(e) =>
                      onFormChange({ ...form, joined_on: e.target.value })
                    }
                  />
                </label>
              ) : null}
              <button
                type="submit"
                className="btn-add-member"
                disabled={adding}
              >
                {adding ? 'Добавление…' : 'Добавить'}
              </button>
            </form>
          )}

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Закрыть
            </button>
          </div>
        </div>
      </ModalOverlay>
    </ModalPortal>
  );
}
